/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useMemo} from "react";
import { SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS } from "../constants";
import { Container } from "../common/Container";
import AppBar from "../../../components/AppBar/AppBar";
import Typography from "@mui/material/Typography";
import { ISetRecentlyViewedParams, useSetRecentlyViewedWithDeps } from "../../../../hooks/useSetRecentlyViewedWithDeps";
import { ETrackedItemTypes } from "../../../../constants/trackedItems";
import { useSearchParams } from "react-router-dom";
import { ExperimentsPanel } from "./ExperimentsPanel/ExperimentsPanel";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  fetchExperiments,
  fetchStudies as fetchArchitectureStudies,
  resetGreenlineAppReduxState,
  selectExperimentId,
  selectStudy,
  setCurrentStudyId,
  setSensorIdToName
} from "../../../../redux/ducks/SensingSystemArchitectureDesign";
import { ConnectAdasProgramWizard } from "./Dialogs/ConnectAdasProgramWizard/ConnectAdasProgramWizard";
import {useFetchToken, usePostRequest} from "../../../../api/helper";
import toast from "react-hot-toast";
import CloseGlobalDialogButton from "../../../components/Global/Dialog/CloseGlobalDialogButton";
import * as React from "react";
import {selectGlobalDialogState} from "../../../../redux/ducks/GlobalDialog/slice";


/**
 * Root component for the Sensing System Architecture Design view study page
 */
const ViewStudyPage = () => {
  const dispatch = useAppDispatch()

  /** Read study ID from URL */
  const [searchParams, setSearchParams] = useSearchParams()
  const urlParams = Object.fromEntries(searchParams)
  const paramobj: any = useAppSelector(state => {
    const dialogState = selectGlobalDialogState(state)
    if (dialogState.isOpen) {
      return dialogState.component?.params ?? urlParams
    } else return urlParams
  })
  const studyId = paramobj?.[SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.constants.queryStringKeys.id] ?? '';

  const fetchToken = useFetchToken()

  // Get map of sensor id to sensor name
  const reqGetSensorIdToName = usePostRequest(SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.getSensorIdToName)

  useEffect(() => {
    const fetchArchitectureStudiesData = async () => {
      const tokenFunction = await fetchToken
      const idToken = await tokenFunction()
      dispatch(fetchArchitectureStudies(idToken))
    }
    fetchArchitectureStudiesData()
  }, [])

  useEffect(() => {
    if (studyId) {
      // Reset redux state
      dispatch(resetGreenlineAppReduxState())

      dispatch(setCurrentStudyId(studyId))
      const fetchExperimentsData = async (studyId: string) => {
        const tokenFunction = await fetchToken
        const idToken = await tokenFunction()
        dispatch(fetchExperiments({idToken, studyId}))
      }
      fetchExperimentsData(studyId)

      // Get sensor id to name mapping
      reqGetSensorIdToName()
      .then((response) => {
        dispatch(setSensorIdToName(response))
      }).catch((err) => {
        console.log(err)
      })
    }
  }, [studyId])


  /** Select study data from study ID */
  const study = useAppSelector(state => selectStudy(state, studyId))
  const studyName = study?.name ?? ''

  /** Update user's 'Recently Viewed' items */
  const recentlyViewedParams: ISetRecentlyViewedParams = {
    trackedItemUid: studyId,
    partitionKey: studyId,
    trackedItemType: ETrackedItemTypes.SENSING_SYSTEM_ARCHITECTURE_DESIGN,
  }
  useSetRecentlyViewedWithDeps(recentlyViewedParams)

  useEffect(() => {
    if(studyName)
      document.title = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.landing.title + ' - ' + studyName;
    return () => {
      document.title = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.landing.title + ' - Landing';
    };
  }, [studyName]);

  return (
    <Container>
      <AppBar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#ffffff'}}>
          {`${studyName} Study`}
        </Typography>
        <CloseGlobalDialogButton/>
      </AppBar>
      <Container>
        <ExperimentsPanel />
      </Container>
      <ConnectAdasProgramWizard />
    </Container>
  )
}

/** Using default export is required for lazy loading */
export default ViewStudyPage
